import { Link } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import { TreeHero } from "@/components/TreeHero";
import { EraJourney } from "@/components/EraJourney";
import { Eyebrow, Reveal } from "@/components/Motion";
import { useSite } from "@/context/SiteContext";

export const Marquee = ({ text }) => (
  <div className="relative overflow-hidden border-y border-[#C9A24B]/25 bg-[#0B1D14] py-7" data-testid="editorial-marquee">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {[text, "1890 — 2026", "From the Highveld to Texas", text, "Six generations, one tree"].map((t, i) => (
            <span key={i} className="flex items-center font-accent italic text-3xl md:text-5xl text-[#E4C87F]/90 px-8 whitespace-nowrap">{t}<span className="ml-16 text-[#C9A24B] text-xl not-italic">✦</span></span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const DOORS = [
  ["/story", "Our Story", "A timeline from the first roots in 1890 to today."],
  ["/tree", "The Family Tree", "Every leaf, private by default. Hover to meet them."],
  ["/memoriam", "In Memoriam", "Those who came before us, always part of our tree."],
  ["/voices", "Family Voices", "Hear their stories, in their own words."],
  ["/places", "Where We've Lived", "Towns and regions across two continents."],
  ["/gallery", "Gallery", "The public album, opened slowly."],
];

export default function Home() {
  const { site } = useSite() || {};
  return (
    <div data-testid="home-page">
      <TreeHero />
      <Marquee text={site?.slogan || "Every leaf or every branches makes a great tree"} />
      <EraJourney />
      <section className="bg-[#0B1D14] py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal><Eyebrow>Wander the archive</Eyebrow><h2 className="mt-5 text-4xl sm:text-5xl font-display tracking-tight max-w-2xl">Every room of the house, with the lights on low.</h2></Reveal>
          <div className="mt-16 grid gap-px bg-[#C9A24B]/20 border border-[#C9A24B]/20 rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-3">
            {DOORS.map(([to, t, l], i) => (
              <Reveal key={to} delay={(i % 3) * 0.08} className="bg-[#0B1D14]">
                <Link to={to} className="group block p-8 md:p-10 h-full hover:bg-[#163B29]/60 transition-colors duration-500" data-testid={`home-door-${to.slice(1)}`}>
                  <div className="flex items-start justify-between">
                    <span className="font-mono-x text-xs text-[#C9A24B]">0{i + 1}</span>
                    <ArrowUpRight className="text-[#C9A24B] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" size={20} />
                  </div>
                  <h3 className="mt-10 font-display text-2xl md:text-3xl text-[#F6EFE0]">{t}</h3>
                  <p className="mt-3 text-sm text-[#F6EFE0]/75">{l}</p>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-20">
            <Link to="/gate" className="group flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-2xl border border-[#C9A24B]/40 p-8 md:p-12 bg-[radial-gradient(ellipse_at_left,rgba(201,162,75,.18),transparent_60%)]" data-testid="home-gate-cta">
              <div className="flex items-center gap-6">
                <span className="grid place-items-center w-16 h-16 rounded-full border border-[#C9A24B] text-[#C9A24B]"><Lock size={22} /></span>
                <div><p className="font-display text-2xl md:text-3xl">Family Members Only</p><p className="mt-1 text-sm text-[#F6EFE0]/75">Enter your family member code to view details or history.</p></div>
              </div>
              <span className="btn-gold w-fit">Open the gate</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
